import { createFileRoute, Link, notFound, Outlet } from '@tanstack/react-router'
import { EmptyState } from '@/components/empty-state'
import { Button } from '@/components/ui/button'
import { setRequestOrganization } from '@/lib/api'
import { authClient } from '@/lib/auth-client'
import { isReservedSegment, meQuery, rememberWorkspace, sitesQuery } from '@/lib/queries'

export const Route = createFileRoute('/_app/$org')({
  beforeLoad: async ({ context, params }) => {
    if (isReservedSegment(params.org)) throw notFound()

    const me = await context.queryClient.ensureQueryData(meQuery)
    const organization = me.organizations.find((candidate) => candidate.slug === params.org)
    if (!organization) throw notFound()

    // Every request from here on names this workspace explicitly, so it is
    // scoped by the URL rather than by whichever tab switched most recently.
    // Set first: even the invalidation refetches below go out with it.
    setRequestOrganization(organization.id)

    // Better Auth's own organization endpoints still read the session's active
    // org, so the session is brought into line too — and awaited, because a
    // child route must not load against the workspace we are leaving.
    if (organization.id !== me.activeOrganizationId) {
      const { error } = await authClient.organization.setActive({
        organizationId: organization.id,
      })
      if (error) throw new Error(error.message ?? 'Could not open this workspace')
      // Everything already cached was read as the workspace we just left.
      await context.queryClient.invalidateQueries()
    }

    rememberWorkspace(organization.slug)
    return { organization }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(sitesQuery),
  component: Outlet,
  notFoundComponent: WorkspaceNotFound,
})

function WorkspaceNotFound() {
  return (
    <div className="rounded-lg border border-border bg-card">
      <EmptyState
        title="Workspace not found"
        description="That workspace does not exist, or your account is not a member of it."
        action={
          <Button asChild size="sm" variant="outline">
            <Link to="/">Go to your workspace</Link>
          </Button>
        }
      />
    </div>
  )
}
